import React, {useState, useEffect, FC} from 'react';
import {ChevronIcon, CopyIcon, ScanTextIcon} from 'elements/icons';
import './styles.css';
import {Selector} from 'elements/selector';
import {message} from 'antd';
import {OnboardingTooltip} from 'elements/tooltip';
import {Button} from '../../Login';
import {PermissionGuard} from 'elements/PermissionGuard';
import {useStores} from "stores/root";
import {IUserSettings} from "types";
import {observer} from "mobx-react-lite";


const transformDateTime = (date: Date) => {
    const now = new Date();
    const inputDate = new Date(date);

    if (inputDate.toDateString() === now.toDateString()) {
        const time = inputDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        return {
            monthDay: 'Today',
            time: time
        };
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (inputDate.toDateString() === yesterday.toDateString()) {
        const time = inputDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        return {
            monthDay: 'Yesterday',
            time: time
        };
    }

    const month = inputDate.toLocaleString('en-US', {month: 'short'});
    const day = inputDate.getDate();
    const time = inputDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });

    return {
        monthDay: `${month}, ${day}`,
        time: time
    };
}


export default observer(function HistoryPage(props) {
    const {history, input} = useStores()
    return <div>
        {
            history.loading || !history.history ? <div>Loading...</div> :
                <History onScanAgain={(text) => {input.setText(text)}}/>
        }
    </div>;
})


function HistoryHeader() {
    return <div className='history-header-container history-header-color'>
        <div className='history-header-item-first'>
            <span className='history-header-item-text'>Date</span>
            <span className='history-header-item-text'>Text</span>
        </div>
        <div className='history-header-item-second'>
            <span className='history-header-item-text history-header-item-text-last'>Actions</span>
            <span className='history-header-item-text'>AI Scan</span>
            <span className='history-header-item-text'>Plagiarism</span>
        </div>
    </div>
}


const History = observer(({onScanAgain}: {
    onScanAgain: (item: string) => void
}) => {
    const [messageApi, contextHolder] = message.useMessage();
    const {history, user: userStore} = useStores()
    const user = userStore.user as IUserSettings | null
    return <div className="centered">
        {contextHolder}
        <div className="history-content">
            <div className="history-header-container">
                <OnboardingTooltip id={3}>
                    <div className='history-header'>History</div>
                </OnboardingTooltip>
                <div className="pag-ext"><PaginationSettings
                    total={history.totalPages}
                    setPageSize={history.setPageSize as (pageSize: number) => void}
                    pageSize={history.pageSize}
                /></div>
            </div>
            <HistoryHeader/>
            <div className={'history-page ' + (!user?.history_retention ? 'blur' : '')}>
                {history.history.length > 0 ? (
                    history.history.map((item) => (
                        <HistoryItem
                            key={item.id}
                            item={item}
                            onCopy={() => {
                                messageApi.open({
                                    type: 'success',
                                    content: 'message copied to clipboard!',
                                    duration: 2,
                                });
                            }}
                            onScanAgain={() => {
                                onScanAgain(item.text);
                                document.getElementById("scan-layout")?.scrollTo({top: 0, behavior: 'smooth'})
                            }}
                        />
                    ))
                ) : (
                    <div className='table__empty'>
                        <span>Nothing is here yet. Start by scanning some text!</span>
                        <Button className='empty__button' onClick={() => window.location.href = '/main/scan'}>
                            <ScanTextIcon/>
                            <span className='empty__button-text'>Scan Text</span>
                        </Button>
                    </div>
                )}
            </div>
            <div style={{height: 20}}></div>
            <Pagination total={history.totalPages} pageSize={history.pageSize} current={history.page}
                        setCurrentPage={history.setPage}/>
            <div style={{height: 20}}></div>
            {
                !user?.history_retention ? <PermissionGuard onUnlock={() => {
                    window.location.href = '/main/scan';
                }}/> : null
            }
        </div>
    </div>
})


const HistoryItem = ({item, onCopy, onScanAgain}: {
    item: any,
    onCopy: () => void,
    onScanAgain: (text: string) => void
}) => {
    const [expanded, setExpanded] = useState(false);

    const text = expanded ? item.text : item.text.slice(0, 70) + '...';
    const timeObject = transformDateTime(new Date(item.date_created));

    return (
        <div className='history-item' style={{display: 'flex'}}>
            <div className="history-item-section">
                <div className="history-content-date" onClick={() => {
                    setExpanded(!expanded)
                }} style={{cursor: 'pointer'}}>
                    <div style={{transform: expanded ? 'rotate(90deg)' : '', width: '20px', height: '20px'}}>
                        <ChevronIcon/>
                    </div>
                    <span style={{display: 'inline-block', marginLeft: '10px', width: '125px'}}>
            <span>{(timeObject as any).monthDay} </span>
            <span className='history-content-date-time'>{(timeObject as any).time}</span>
          </span>
                </div>
                <div className="history-content-text">
                    {text}
                </div>
            </div>
            <div className="history-item-tools hitory-item-section">
                <div style={{cursor: 'pointer'}} onClick={() => {
                    navigator.clipboard.writeText(item.text);
                    onCopy();
                }}><CopyIcon/></div>
                <button className="secondary history-btn" onClick={() => {
                    onScanAgain(item.text);
                }}>Scan again
                </button>
                {
                    item.answer != -1 ? <AIWidget score={item.answer * 100} suffix=' '/> : <span
                        style={{width: 74, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>-</span>
                }
                {
                    item.plagiarism_data != null ? <AIWidget score={item.plagiarism_data.score} suffix=' '/> : <span
                        style={{width: 74, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>-</span>
                }
            </div>
        </div>
    );
}

interface PaginationProps {
    total: number;
    pageSize: number;
    current: number;
    setCurrentPage: (page: number) => void;
    size?: number;
    showControllButtons?: boolean;
}

export const Pagination = (props: PaginationProps) => {
    const totalPages = Math.ceil(props.total / props.pageSize);
    const [pages, setPages] = useState<JSX.Element[]>([]);
    const size = props.size || 48;
    const showControllButtons = props.showControllButtons === undefined ? true : props.showControllButtons;

    useEffect(() => {
        const newPages = [];

        // First page
        if (showControllButtons) {
            newPages.push(
                <button
                    key="first"
                    className="secondary small"
                    onClick={() => props.setCurrentPage(1)}
                    disabled={props.current === 1}
                    style={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0px'}}
                >
                    «
                </button>
            );
        }

        // Previous page
        newPages.push(
            <button
                key="prev"
                className="secondary small"
                onClick={() => props.setCurrentPage(props.current - 1)}
                disabled={props.current === 1}
                style={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0px'}}
            >
                ‹
            </button>
        );

        let startPage = Math.max(1, props.current - 2);
        let endPage = Math.min(totalPages, startPage + 4);

        if (endPage - startPage < 4) {
            startPage = Math.max(1, endPage - 4);
        }

        for (let i = startPage; i <= endPage; i++) {
            newPages.push(
                <button
                    key={i}
                    className={`secondary small ${props.current === i ? 'primary primary-pagination' : ''}`}
                    onClick={() => props.setCurrentPage(i)}
                    style={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0px'}}
                >
             {i}
                </button>
            );
        }

        // Add ellipsis and last page if needed
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                newPages.push(
                    <span key="ellipsis" style={{padding: '0 4px'}}>...</span>
                );
            }
            newPages.push(
                <button
                    key={totalPages}
                    className={`secondary small ${props.current === totalPages ? 'primary' : ''}`}
                    onClick={() => props.setCurrentPage(totalPages)}
                    style={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0px'}}
                >
                    {totalPages}
                </button>
            );
        }

        // Next page
        newPages.push(
            <button
                key="next"
                className="secondary small"
                onClick={() => props.setCurrentPage(props.current + 1)}
                disabled={props.current === totalPages}
                style={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0px'}}
            >
                ›
            </button>
        );

        // Last page
        if (showControllButtons) {
            newPages.push(
                <button
                    key="last"
                    className="secondary small"
                    onClick={() => props.setCurrentPage(totalPages)}
                    disabled={props.current === totalPages}
                    style={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0px'}}
                >
                    »
                </button>
            );
        }

        setPages(newPages);
    }, [props.current, totalPages, props.setCurrentPage]);

    return (
        <div style={{display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center'}}>
            {pages}
        </div>
    );
}

interface IAIWidget {
    score: number;
    suffix?: string;
}

const AIWidget = (props: IAIWidget) => {
    const {score, suffix} = props;
    const suffixText = suffix ? ` ${suffix}` : 'AI';

    return <div
        className={'score ' + (score <= 40 ? 'success-score' : score <= 60 ? 'warning-score' : 'danger-score')}>{score.toFixed(0)}% {suffixText}</div>
}


interface IPagination {
    total: number;
    setPageSize: (pageSize: number) => void;
    pageSize: number;
}

const PaginationSettings: FC<IPagination> = (props) => {
    return <div className='pagination-settings__container'>
        <span>Display results</span>
        <Selector options={['10', '20', '50', '100']} onSelect={(a) => {
            props.setPageSize(Number.parseInt(a));
        }} sharp
                  active={props.pageSize.toString()}
        />
        <span>out of {props.total}</span>
    </div>
}